import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { getActivityTypeLabel } from '../../../../constants/labels';
import { useUpdateActivityTypeMutation } from '../../../../hooks/mutations/useActivityTypeMutation';
import { useActivitiesTypesQuery } from '../../../../hooks/queries/useActivitiesTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { ActivityType } from '../../../../services/apiSLTP/dailyReports/activityTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateActivityTypeModal from '../../../modals/admin/configuration/UpdateActivityTypeModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface ActivityTypesSettingsRowProps {
    withUsageCount?: boolean;
}

const ActivityTypesSettingsRow: React.FC<ActivityTypesSettingsRowProps> = ({ withUsageCount = false }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedActivityType, setSelectedActivityType] = useState<ActivityType | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataActivityTypes, isLoading: isActivityTypesLoading, refetch: refetchActivityTypes } = useActivitiesTypesQuery({});

    const { mutate: mutateActivityType } = useUpdateActivityTypeMutation({
        onSuccess: () => refetchActivityTypes(),
        onError: (error) => console.error('Error updating activity type:', error.message),
    });

    const sortedActivityTypes = useMemo(() => {
        return (
            dataActivityTypes?.sort((a, b) => {
                const designationA = a?.designation || '';
                const designationB = b?.designation || '';
                return designationA.localeCompare(designationB);
            }) || []
        );
    }, [dataActivityTypes]);

    const columnDefs: ColDef<ActivityType>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 300,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<ActivityType>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={t('modifyActivityType')}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedActivityType(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={t('deleteActivityType')}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedActivityType(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<ActivityType>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            valueGetter: (params) => capitalizeFirstLetter(getActivityTypeLabel(params.data?.type)) || '',
            colId: 'type',
        },
        createDateTimeColumn<ActivityType>('createdAt', 'createdAt'),
        createDateTimeColumn<ActivityType>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push({
            headerName: t('_id'),
            field: '_id',
            colId: '_id',
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
            initialWidth: 250,
        });
    }

    return (
        <>
            <GenericAgGridTable
                rowData={sortedActivityTypes}
                columnDefs={columnDefs}
                loading={isActivityTypesLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedActivityType(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={t('addActivityType')}
                    />
                }
                exportCsvFileName="admin-activity-types"
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateActivityTypeModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    activityType={selectedActivityType}
                    refetch={refetchActivityTypes}
                />
            )}

            {showDeleteModal && selectedActivityType && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateActivityType({
                            activityTypeId: selectedActivityType._id,
                            data: { crudStatus: crudStatusEnum.DELETED },
                        });
                    }}
                    title={t('activityType')}
                    itemName={selectedActivityType.designation}
                />
            )}
        </>
    );
};

export default ActivityTypesSettingsRow;
